// IntegrationsTab.js
// Drop this file into src/ and import it in App.js
// Usage: import IntegrationsTab from './IntegrationsTab';
// Then add to getActiveTabContent(): case 'integrations': return <IntegrationsTab user={user} getAuthToken={getAuthToken} />;
// And add to tabs array: { id: 'integrations', label: 'AW Integrations', icon: '🔗' }

import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';

export default function IntegrationsTab({ user, getAuthToken }) {
  // ─── Connection State ───────────────────────────────────────────────────────
  const [integrationStatus, setIntegrationStatus] = useState({
    procore: { connected: false, loading: true, companyName: null },
    autodesk: { connected: false, loading: true, hubName: null }
  });

  // ─── File Browser State ─────────────────────────────────────────────────────
  const [activePlatform, setActivePlatform] = useState(null); // 'procore' | 'autodesk'
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [specFiles, setSpecFiles] = useState([]);
  const [submittals, setSubmittals] = useState([]);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [selectedSubmittal, setSelectedSubmittal] = useState(null);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserError, setBrowserError] = useState(null);

  // ─── Job State ──────────────────────────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  // ─── Webhook Subscriptions ──────────────────────────────────────────────────
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);

  // ─── Disconnect Confirmation ─────────────────────────────────────────────────
  const [disconnectConfirm, setDisconnectConfirm] = useState(null); // 'procore' | 'autodesk' | null

  // ─── Auth Helper ────────────────────────────────────────────────────────────
  const getToken = useCallback(() => {
    // Use the passed getAuthToken if available, otherwise fall back to localStorage
    if (getAuthToken) return getAuthToken();
    return localStorage.getItem('auth0_token');
  }, [getAuthToken]);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${getToken()}`,
    'Content-Type': 'application/json'
  }), [getToken]);

  // ─── Load Integration Status ────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      const userId = user?.email || user?.sub || 'anonymous';
      const res = await fetch(`${API_BASE}/api/integrations/status?userId=${encodeURIComponent(userId)}`, {
        headers: authHeaders()
      });
      if (!res.ok) {
        setIntegrationStatus(prev => ({
          procore: { ...prev.procore, loading: false },
          autodesk: { ...prev.autodesk, loading: false }
        }));
        return;
      }
      const data = await res.json();
      const c = data.connections || data;
      setIntegrationStatus({
        procore: {
          connected: c.procore?.connected || false,
          loading: false,
          companyName: c.procore?.companyName || null
        },
        autodesk: {
          connected: c.autodesk?.connected || false,
          loading: false,
          hubName: c.autodesk?.companyName || null
        }
      });
    } catch (err) {
      setIntegrationStatus(prev => ({
        procore: { ...prev.procore, loading: false },
        autodesk: { ...prev.autodesk, loading: false }
      }));
    }
  }, [authHeaders, user]);

  useEffect(() => {
    loadStatus();
    // Check for OAuth callback redirect
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('integration');
    if (oauthSuccess) {
      window.history.replaceState({}, document.title, window.location.pathname);
      loadStatus();
    }
  }, [loadStatus]);

  // ─── Load Jobs ──────────────────────────────────────────────────────────────
  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/analysis/jobs?limit=10`, {
        headers: authHeaders()
      });
      if (!res.ok) return;
      const data = await res.json();
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setJobsLoading(false);
    }
  }, [authHeaders]);

  // ─── Load Subscriptions ─────────────────────────────────────────────────────
  const loadSubscriptions = useCallback(async () => {
    setSubscriptionsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/webhooks/subscriptions`, {
        headers: authHeaders()
      });
      if (!res.ok) return;
      const data = await res.json();
      setSubscriptions(data.subscriptions || []);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [authHeaders]);

  // ─── Connect Platform ────────────────────────────────────────────────────────
const connectPlatform = async (platform) => {
  try {
    const userId = user?.email || user?.sub || 'anonymous';
    const res = await fetch(
      `${API_BASE}/api/integrations/${platform}/connect?userId=${encodeURIComponent(userId)}`,
      { headers: authHeaders() }
    );
    if (!res.ok) return;
    const data = await res.json();
    if (data.authUrl) {
      window.location.href = data.authUrl;
    }
  } catch (err) {
    console.error(`Failed to connect ${platform}:`, err);
  }
};

  // ─── Disconnect Platform ─────────────────────────────────────────────────────
  const disconnectPlatform = (platform) => {
    setDisconnectConfirm(platform);
  };

  const confirmDisconnect = async () => {
    const platform = disconnectConfirm;
    setDisconnectConfirm(null);
    try {
      const userId = user?.email || user?.sub || 'anonymous';
      await fetch(`${API_BASE}/api/integrations/${platform}/disconnect?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      setIntegrationStatus(prev => ({
        ...prev,
        [platform]: { connected: false, loading: false }
      }));
      if (activePlatform === platform) {
        setActivePlatform(null);
        setProjects([]);
        setSelectedProject(null);
      }
    } catch (err) {
      console.error(`Failed to disconnect ${platform}:`, err);
    }
  };

  // ─── Open File Browser ───────────────────────────────────────────────────────
  const openBrowser = async (platform) => {
    setActivePlatform(platform);
    setSelectedProject(null);
    setSpecFiles([]);
    setSubmittals([]);
    setSelectedSpec(null);
    setSelectedSubmittal(null);
    setBrowserError(null);
    setBrowserLoading(true);
    loadJobs();
    loadSubscriptions();

    try {
      const res = await fetch(`${API_BASE}/api/files/${platform}/projects`, {
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Failed to load projects');
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (err) {
      setBrowserError('Could not load projects. Please check your connection.');
    } finally {
      setBrowserLoading(false);
    }
  };

  // ─── Select Project ──────────────────────────────────────────────────────────
  const selectProject = async (project) => {
    setSelectedProject(project);
    setSpecFiles([]);
    setSubmittals([]);
    setSelectedSpec(null);
    setSelectedSubmittal(null);
    setBrowserLoading(true);
    setBrowserError(null);

    try {
      if (activePlatform === 'procore') {
        const [docsRes, submittalsRes] = await Promise.all([
          fetch(`${API_BASE}/api/files/procore/projects/${project.id}/documents`, { headers: authHeaders() }),
          fetch(`${API_BASE}/api/files/procore/projects/${project.id}/submittals`, { headers: authHeaders() })
        ]);
        const docs = docsRes.ok ? await docsRes.json() : { documents: [] };
        const subs = submittalsRes.ok ? await submittalsRes.json() : { submittals: [] };
        setSpecFiles(docs.documents || []);
        setSubmittals(subs.submittals || []);
      } else {
        const foldersRes = await fetch(`${API_BASE}/api/files/autodesk/projects/${project.id}/folders`, {
          headers: authHeaders()
        });
        const folders = foldersRes.ok ? await foldersRes.json() : { folders: [] };
        // For Autodesk, top folders become both spec and submittal source
        setSpecFiles(folders.folders || []);
        setSubmittals(folders.folders || []);
      }
    } catch (err) {
      setBrowserError('Could not load project files.');
    } finally {
      setBrowserLoading(false);
    }
  };

  // ─── Trigger Analysis ────────────────────────────────────────────────────────
  const triggerAnalysis = async () => {
    if (!selectedSpec || !selectedSubmittal) return;
    setAnalysisLoading(true);
    setAnalysisError(null);

    try {
      const body = activePlatform === 'procore'
        ? {
            projectId: selectedProject.id,
            projectName: selectedProject.name,
            specFileId: selectedSpec.id,
            specFileName: selectedSpec.name,
            specDownloadUrl: selectedSpec.downloadUrl,
            submittalId: selectedSubmittal.id,
            submittalFileName: selectedSubmittal.title || selectedSubmittal.name,
            submittalDownloadUrl: selectedSubmittal.downloadUrl,
            pushReport: true
          }
        : {
            projectId: selectedProject.id,
            projectName: selectedProject.name,
            specItemId: selectedSpec.id,
            specFileName: selectedSpec.name,
            submittalItemId: selectedSubmittal.id,
            submittalFileName: selectedSubmittal.name,
            pushReport: true
          };

      const res = await fetch(`${API_BASE}/api/analysis/${activePlatform}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error('Analysis failed to start');
      const data = await res.json();
      setActiveJobId(data.jobId);
      loadJobs();

      // Reset selections
      setSelectedSpec(null);
      setSelectedSubmittal(null);

    } catch (err) {
      setAnalysisError('Analysis failed to start. Please try again.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  // ─── Toggle Webhook Subscription ────────────────────────────────────────────
  const toggleSubscription = async (platform, projectId, projectName) => {
    const existing = subscriptions.find(s => s.platform === platform && s.projectId === projectId);
    try {
      if (existing) {
        await fetch(`${API_BASE}/api/webhooks/subscribe`, {
          method: 'DELETE',
          headers: authHeaders(),
          body: JSON.stringify({ platform, projectId })
        });
      } else {
        await fetch(`${API_BASE}/api/webhooks/subscribe`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ platform, projectId, projectName })
        });
      }
      loadSubscriptions();
    } catch (err) {
      console.error('Toggle subscription failed:', err);
    }
  };

  const isSubscribed = (platform, projectId) =>
    subscriptions.some(s => s.platform === platform && s.projectId === projectId && s.active);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const getJobStatusColor = (status) => {
    switch (status) {
      case 'complete': return { bg: '#dcfce7', color: '#166534', label: '✅ Complete' };
      case 'processing': return { bg: '#dbeafe', color: '#1e40af', label: '⚙️ Processing' };
      case 'downloading': return { bg: '#fef9c3', color: '#854d0e', label: '⬇️ Downloading' };
      case 'failed': return { bg: '#fee2e2', color: '#991b1b', label: '❌ Failed' };
      default: return { bg: '#f3f4f6', color: '#374151', label: '⏳ Queued' };
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // ─── Styles ───────────────────────────────────────────────────────────────────
  const S = {
    section: {
      marginBottom: '28px',
      padding: '20px',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '10px'
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    platformCard: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      backgroundColor: 'white',
      border: '2px solid #e5e7eb',
      borderRadius: '10px',
      marginBottom: '12px',
      transition: 'border-color 0.2s'
    },
    platformCardConnected: {
      borderColor: '#D4AF37',
      backgroundColor: '#fffbeb'
    },
    platformName: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#000000',
      marginBottom: '2px'
    },
    platformDetail: {
      fontSize: '12px',
      color: '#6b7280'
    },
    btnGold: {
      backgroundColor: '#D4AF37',
      color: '#000000',
      fontWeight: '600',
      padding: '8px 18px',
      borderRadius: '7px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '13px',
      transition: 'background-color 0.2s',
      whiteSpace: 'nowrap'
    },
    btnBlack: {
      backgroundColor: '#000000',
      color: '#D4AF37',
      fontWeight: '600',
      padding: '8px 18px',
      borderRadius: '7px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '13px',
      transition: 'opacity 0.2s',
      whiteSpace: 'nowrap'
    },
    btnGhost: {
      backgroundColor: 'transparent',
      color: '#dc2626',
      fontWeight: '600',
      padding: '6px 14px',
      borderRadius: '7px',
      border: '1px solid #fecaca',
      cursor: 'pointer',
      fontSize: '12px',
      marginLeft: '8px'
    },
    fileList: {
      maxHeight: '220px',
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '7px',
      backgroundColor: 'white'
    },
    fileItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 14px',
      borderBottom: '1px solid #f3f4f6',
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      fontSize: '13px'
    },
    fileItemSelected: {
      backgroundColor: '#fffbeb',
      borderLeft: '3px solid #D4AF37'
    },
    pill: {
      padding: '3px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600'
    },
    projectBtn: {
      width: '100%',
      textAlign: 'left',
      padding: '11px 14px',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '7px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      color: '#000000',
      marginBottom: '6px',
      transition: 'all 0.15s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    projectBtnSelected: {
      backgroundColor: '#fffbeb',
      borderColor: '#D4AF37',
      fontWeight: '600'
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── DISCONNECT CONFIRMATION MODAL ── */}
      {disconnectConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '32px',
            maxWidth: '420px', width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
            border: '2px solid #e5e7eb'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>
                {disconnectConfirm === 'procore' ? '🏗️' : '🔷'}
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#000', marginBottom: '8px' }}>
                Disconnect {disconnectConfirm === 'procore' ? 'Procore' : 'Autodesk'}?
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.5' }}>
                This will remove your stored credentials and you will need to reconnect to browse files from this platform.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setDisconnectConfirm(null)}
                style={{
                  flex: 1, padding: '10px', fontSize: '14px', fontWeight: '600',
                  backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDisconnect}
                style={{
                  flex: 1, padding: '10px', fontSize: '14px', fontWeight: '600',
                  backgroundColor: '#dc2626', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                Yes, Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── INTRO BANNER ── */}
      <div style={{
        marginBottom: '24px',
        padding: '18px 20px',
        backgroundColor: '#fffbeb',
        border: '2px solid #D4AF37',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px'
      }}>
        <span style={{ fontSize: '28px', flexShrink: 0 }}>🔗</span>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#000000', marginBottom: '4px' }}>
            Direct Platform Integrations
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.5' }}>
            Connect Procore or Autodesk to pull spec and submittal files directly into AECWorks AI — no manual uploads.
            After analysis, AECWorks AI pushes the compliance report back to your platform automatically.
          </div>
        </div>
      </div>

      {/* ── CONNECTIONS ── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>
          <span>🔌</span> Connected Platforms
        </div>

        {/* Procore */}
        <div style={{
          ...S.platformCard,
          ...(integrationStatus.procore.connected ? S.platformCardConnected : {})
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '42px', height: '42px',
              backgroundColor: integrationStatus.procore.connected ? '#D4AF37' : '#f3f4f6',
              borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px'
            }}>🏗️</div>
            <div>
              <div style={S.platformName}>Procore</div>
              <div style={S.platformDetail}>
                {integrationStatus.procore.loading
                  ? 'Checking...'
                  : integrationStatus.procore.connected
                    ? `✅ Connected${integrationStatus.procore.companyName ? ` — ${integrationStatus.procore.companyName}` : ''}`
                    : 'Not connected'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {integrationStatus.procore.connected ? (
              <>
                <button style={S.btnGold} onClick={() => openBrowser('procore')}>
                  📂 Browse Files
                </button>
                <button style={S.btnGhost} onClick={() => disconnectPlatform('procore')}>
                  Disconnect
                </button>
              </>
            ) : (
              <button
                style={S.btnGold}
                disabled={integrationStatus.procore.loading}
                onClick={() => connectPlatform('procore')}
              >
                {integrationStatus.procore.loading ? 'Loading...' : 'Connect Procore'}
              </button>
            )}
          </div>
        </div>

        {/* Autodesk */}
        <div style={{
          ...S.platformCard,
          ...(integrationStatus.autodesk.connected ? S.platformCardConnected : {}),
          marginBottom: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '42px', height: '42px',
              backgroundColor: integrationStatus.autodesk.connected ? '#D4AF37' : '#f3f4f6',
              borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '20px'
            }}>🔷</div>
            <div>
              <div style={S.platformName}>Autodesk Construction Cloud</div>
              <div style={S.platformDetail}>
                {integrationStatus.autodesk.loading
                  ? 'Checking...'
                  : integrationStatus.autodesk.connected
                    ? `✅ Connected${integrationStatus.autodesk.hubName ? ` — ${integrationStatus.autodesk.hubName}` : ''}`
                    : 'Not connected'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {integrationStatus.autodesk.connected ? (
              <>
                <button style={S.btnGold} onClick={() => openBrowser('autodesk')}>
                  📂 Browse Files
                </button>
                <button style={S.btnGhost} onClick={() => disconnectPlatform('autodesk')}>
                  Disconnect
                </button>
              </>
            ) : (
              <button
                style={S.btnGold}
                disabled={integrationStatus.autodesk.loading}
                onClick={() => connectPlatform('autodesk')}
              >
                {integrationStatus.autodesk.loading ? 'Loading...' : 'Connect Autodesk'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── FILE BROWSER ── */}
      {activePlatform && (
        <div style={S.section}>
          <div style={S.sectionTitle}>
            <span>📂</span>
            {activePlatform === 'procore' ? 'Procore' : 'Autodesk'} File Browser
            <button
              onClick={() => setActivePlatform(null)}
              style={{ ...S.btnGhost, marginLeft: 'auto', color: '#6b7280', borderColor: '#e5e7eb' }}
            >
              ✕ Close
            </button>
          </div>

          {browserLoading && (
            <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280', fontSize: '14px' }}>
              ⏳ Loading...
            </div>
          )}

          {browserError && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              color: '#991b1b',
              borderRadius: '7px',
              fontSize: '13px',
              marginBottom: '12px'
            }}>
              {browserError}
            </div>
          )}

          {!browserLoading && !browserError && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>

              {/* Column 1 — Projects */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Projects ({projects.length})
                </div>
                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                  {projects.length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px' }}>No projects found</div>
                  ) : projects.map(proj => (
                    <button
                      key={proj.id}
                      onClick={() => selectProject(proj)}
                      style={{
                        ...S.projectBtn,
                        ...(selectedProject?.id === proj.id ? S.projectBtnSelected : {})
                      }}
                    >
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {proj.name}
                      </span>
                      {selectedProject?.id === proj.id && <span style={{ color: '#D4AF37', fontSize: '16px' }}>›</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Column 2 — Spec Files */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Spec Files ({specFiles.length})
                  {selectedSpec && (
                    <span style={{ marginLeft: '8px', color: '#D4AF37', fontWeight: '700' }}>1 selected</span>
                  )}
                </div>
                {!selectedProject ? (
                  <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px' }}>Select a project first</div>
                ) : (
                  <div style={S.fileList}>
                    {specFiles.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px' }}>No spec files found</div>
                    ) : specFiles.map(file => (
                      <div
                        key={file.id}
                        onClick={() => setSelectedSpec(selectedSpec?.id === file.id ? null : file)}
                        style={{
                          ...S.fileItem,
                          ...(selectedSpec?.id === file.id ? S.fileItemSelected : {}),
                          paddingLeft: selectedSpec?.id === file.id ? '11px' : '14px'
                        }}
                      >
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          marginRight: '8px',
                          color: selectedSpec?.id === file.id ? '#000000' : '#374151'
                        }}>
                          📄 {file.name}
                        </span>
                        {selectedSpec?.id === file.id && (
                          <span style={{ ...S.pill, backgroundColor: '#D4AF37', color: '#000000' }}>✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Column 3 — Submittals */}
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Submittals ({submittals.length})
                  {selectedSubmittal && (
                    <span style={{ marginLeft: '8px', color: '#D4AF37', fontWeight: '700' }}>1 selected</span>
                  )}
                </div>
                {!selectedProject ? (
                  <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px' }}>Select a project first</div>
                ) : (
                  <div style={S.fileList}>
                    {submittals.length === 0 ? (
                      <div style={{ fontSize: '12px', color: '#9ca3af', padding: '12px' }}>No submittals found</div>
                    ) : submittals.map(sub => (
                      <div
                        key={sub.id}
                        onClick={() => setSelectedSubmittal(selectedSubmittal?.id === sub.id ? null : sub)}
                        style={{
                          ...S.fileItem,
                          ...(selectedSubmittal?.id === sub.id ? S.fileItemSelected : {}),
                          paddingLeft: selectedSubmittal?.id === sub.id ? '11px' : '14px'
                        }}
                      >
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          marginRight: '8px',
                          color: selectedSubmittal?.id === sub.id ? '#000000' : '#374151'
                        }}>
                          📋 {sub.title || sub.name}
                        </span>
                        {selectedSubmittal?.id === sub.id && (
                          <span style={{ ...S.pill, backgroundColor: '#D4AF37', color: '#000000' }}>✓</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Analyze Button + Webhook Toggle */}
          {selectedProject && (
            <div style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  onClick={triggerAnalysis}
                  disabled={!selectedSpec || !selectedSubmittal || analysisLoading}
                  style={{
                    ...S.btnBlack,
                    opacity: (!selectedSpec || !selectedSubmittal || analysisLoading) ? 0.5 : 1,
                    cursor: (!selectedSpec || !selectedSubmittal || analysisLoading) ? 'not-allowed' : 'pointer',
                    padding: '10px 22px',
                    fontSize: '14px'
                  }}
                >
                  {analysisLoading
                    ? '⏳ Starting...'
                    : !selectedSpec || !selectedSubmittal
                      ? 'Select a Spec + Submittal to Analyze'
                      : '🚀 Run AECWorks AI Analysis'}
                </button>
                {analysisError && (
                  <span style={{ color: '#dc2626', fontSize: '13px' }}>{analysisError}</span>
                )}
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#374151'
              }}>
                <input
                  type="checkbox"
                  checked={isSubscribed(activePlatform, selectedProject?.id)}
                  onChange={() => toggleSubscription(activePlatform, selectedProject?.id, selectedProject?.name)}
                  style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                />
                <span>
                  <strong>Auto-analyze new submittals</strong> in {selectedProject?.name}
                </span>
              </label>
            </div>
          )}

          {activeJobId && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              backgroundColor: '#dcfce7',
              border: '1px solid #bbf7d0',
              borderRadius: '7px',
              fontSize: '13px',
              color: '#166534',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ fontSize: '18px' }}>✅</span>
              <div>
                <strong>Analysis job started!</strong> AECWorks AI is downloading and analyzing your files.
                The compliance report will be pushed back to {activePlatform === 'procore' ? 'Procore' : 'Autodesk'} when complete.
                <button
                  onClick={() => { setActiveJobId(null); loadJobs(); }}
                  style={{ ...S.btnGhost, marginLeft: '12px', color: '#166534', borderColor: '#bbf7d0' }}
                >
                  View Jobs ↓
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── JOB HISTORY ── */}
      <div style={S.section}>
        <div style={{
          ...S.sectionTitle,
          justifyContent: 'space-between'
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📊</span> Analysis Job History
          </span>
          <button
            onClick={loadJobs}
            style={{ ...S.btnGhost, color: '#D4AF37', borderColor: '#D4AF37', fontSize: '12px' }}
          >
            🔄 Refresh
          </button>
        </div>

        {jobsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
            Loading jobs...
          </div>
        ) : jobs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '24px',
            color: '#9ca3af',
            fontSize: '13px',
            backgroundColor: 'white',
            borderRadius: '7px',
            border: '1px dashed #e5e7eb'
          }}>
            No integration jobs yet. Connect a platform above to get started.
          </div>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '7px', overflow: 'hidden', backgroundColor: 'white' }}>
            {jobs.map((job, i) => {
              const statusStyle = getJobStatusColor(job.status);
              return (
                <div key={job._id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderBottom: i < jobs.length - 1 ? '1px solid #f3f4f6' : 'none',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '18px', flexShrink: 0 }}>
                      {job.platform === 'procore' ? '🏗️' : '🔷'}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#000000',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {job.fileName || 'Unknown file'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                        {job.platform === 'procore' ? 'Procore' : 'Autodesk'} · {formatTime(job.startedAt)}
                        {job.triggeredBy === 'webhook' && (
                          <span style={{
                            marginLeft: '8px',
                            padding: '1px 6px',
                            backgroundColor: '#eff6ff',
                            color: '#1d4ed8',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>AUTO</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                    <span style={{
                      ...S.pill,
                      backgroundColor: statusStyle.bg,
                      color: statusStyle.color
                    }}>
                      {statusStyle.label}
                    </span>
                    {job.reportUrl && (
                      <a
                        href={job.reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '12px',
                          color: '#D4AF37',
                          fontWeight: '600',
                          textDecoration: 'none'
                        }}
                      >
                        View Report →
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── AUTO-ANALYSIS SUBSCRIPTIONS ── */}
      {subscriptions.length > 0 && (
        <div style={S.section}>
          <div style={S.sectionTitle}>
            <span>⚡</span> Automatic Analysis — Active Projects
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px', lineHeight: '1.5' }}>
            When a new submittal is uploaded to these projects, AECWorks AI will automatically run spec compliance analysis
            and push the report back to your platform.
          </div>
          {subscriptions.map(sub => (
            <div key={`${sub.platform}-${sub.projectId}`} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              backgroundColor: 'white',
              border: '1px solid #D4AF37',
              borderRadius: '7px',
              marginBottom: '8px',
              fontSize: '13px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span>{sub.platform === 'procore' ? '🏗️' : '🔷'}</span>
                <div>
                  <div style={{ fontWeight: '600', color: '#000000' }}>
                    {sub.projectName || sub.projectId}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    {sub.platform === 'procore' ? 'Procore' : 'Autodesk'} · Auto-analysis enabled
                  </div>
                </div>
              </div>
              <button
                onClick={() => toggleSubscription(sub.platform, sub.projectId)}
                style={S.btnGhost}
              >
                Disable
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}